import { useToast } from '@citizen-walk/shared-ui';
import { formatErrorForToast, fromUnknownError } from '@citizen-walk/shared-types';
import { useNavigate } from 'react-router-dom';
import type { DistrictSummary } from '../district';
import { SessionPicker, type CurrentSessionState } from '../sessions';
import { OutboxList } from './OutboxList';
import { PhotoNoticeModal } from './PhotoNoticeModal';
import { PostForm } from './PostForm';
import { resolvePostFields } from './postFieldsHelpers';
import type { FlushOptions, OutboxState, RetryOneResult } from './useOutbox';

/**
 * AppHome の「投稿」タブパネル (旧 `old/apps/user/src/pages/tabs/PostTab.tsx` 踏襲)。
 *
 * Sprint AC — 旧 PostTab には panel 見出しや「📸 撮影前の注意」inline button は存在せず、
 * カードを順に表示する form のみ。本 panel は組立 (form + outbox + 注意 modal) に限定し、
 * 写真注意の確認は PostForm の `photoAcknowledged=false` 時に出る「注意を確認する」link で
 * 開かれる (旧 PostTab L535-543 + 旧 PhotoNoticeModal flow と同じ pattern)。
 *
 * uid 未取得時は PostForm を render せず placeholder を表示。
 */

export interface PostPanelProps {
  uid: string | null;
  nickname: string;
  photoNoticeAcknowledged: boolean;
  onAcknowledgePhotoNotice: () => void;
  photoNoticeOpen: boolean;
  onPhotoNoticeOpen: (open: boolean) => void;
  outboxState: OutboxState;
  flushOutbox: (
    options?: FlushOptions,
  ) => Promise<{ sent: number; failed: number; skipped: number }>;
  retryOneOutbox: (id: number) => Promise<RetryOneResult>;
  refreshOutbox: () => Promise<void>;
  currentSessionId: string | null;
  district: DistrictSummary;
  /**
   * Phase E (old2 投稿フロー移植): ユーザー設定 (membership.consentAcceptedAt) 未完了か。
   * PostForm が警告バナー + 投稿不可 (old2 PostTab L510-514) に使う。
   */
  userSettingsIncomplete?: boolean;
  /**
   * fvg-2 (docs/111 Wave 1): 現セッション選択の state と setter。
   * session 必須地区で現セッション未選択/失効のとき、本パネルが SessionPicker で再選択導線を出す。
   * 省略時 (任意セッション地区など) は再選択導線を表示しない。
   */
  currentSessionState?: CurrentSessionState;
  onSelectSession?: (id: string | null) => void;
}

export function PostPanel({
  uid,
  nickname,
  photoNoticeAcknowledged,
  onAcknowledgePhotoNotice,
  photoNoticeOpen,
  onPhotoNoticeOpen,
  outboxState,
  flushOutbox,
  retryOneOutbox,
  refreshOutbox,
  currentSessionId,
  district,
  userSettingsIncomplete = false,
  currentSessionState,
  onSelectSession,
}: PostPanelProps) {
  const { toast } = useToast();
  const navigate = useNavigate();

  // fvg-2 (docs/111 Wave 1): session 必須地区で現セッション未選択/失効 → 再選択導線を出す。
  // ready のとき SessionPicker で選び直し、error のとき再読み込み案内を出す
  // (loading 中は一瞬の誤表示を避けるため出さない)。
  const sessionRequiredButMissing =
    !(district.allowPostingWithoutSession ?? false) && !currentSessionId;
  const sessionStatus = currentSessionState?.status;
  const showSessionRecovery =
    sessionRequiredButMissing &&
    (sessionStatus === 'ready' || sessionStatus === 'error') &&
    typeof onSelectSession === 'function';

  return (
    <div className="p-4 max-w-xl mx-auto">
      <PhotoNoticeModal
        open={photoNoticeOpen}
        onAcknowledge={() => {
          onAcknowledgePhotoNotice();
          onPhotoNoticeOpen(false);
          toast('success', '写真撮影の注意を了承しました');
        }}
        onCancel={() => onPhotoNoticeOpen(false)}
      />

      {outboxState.count > 0 && (
        <div className="mb-3 p-2.5 rounded-md bg-notice-50 border border-warning-300 flex items-center justify-between gap-2 flex-wrap">
          <span className="text-xs text-notice-700">
            📤 未送信 <strong>{outboxState.count}</strong> 件
            {outboxState.isFlushing && <span className="ml-2 text-slate-500">(送信中...)</span>}
          </span>
          <button
            type="button"
            onClick={async () => {
              const { sent, failed } = await flushOutbox();
              if (sent > 0) toast('success', `${sent} 件を送信しました`);
              if (failed > 0) toast('error', `${failed} 件の送信に失敗しました`);
            }}
            disabled={outboxState.isFlushing || !navigator.onLine}
            aria-label="未送信の投稿を手動で送信"
            aria-disabled={outboxState.isFlushing || !navigator.onLine}
            className="text-xs text-primary-700 hover:text-primary-700 underline bg-transparent border-none p-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            手動で送信
          </button>
        </div>
      )}

      {/* fvg-2 (docs/111 Wave 1): session 必須地区で現セッション未選択/失効のときの再選択導線。
          終了したセッションに参加していた場合 useCurrentSession が currentSessionId を null に戻すため、
          ここでセッションを選び直すと投稿できるようになる (PostForm のボタン disable も解除される)。 */}
      {showSessionRecovery && currentSessionState && onSelectSession && (
        <div className="mb-3 p-3 rounded-md bg-warning-50 border border-warning-300 text-sm text-warning-800">
          <p className="m-0 mb-2">
            投稿するにはセッションへの参加が必要です。参加していたセッションが終了した場合は、下から選び直してください。
          </p>
          {currentSessionState.status === 'ready' ? (
            <SessionPicker state={currentSessionState} onChange={onSelectSession} />
          ) : (
            // status==='error': 一覧を取得できず選び直せない。内部エラー文言は出さず再読み込みを案内。
            <p className="m-0 text-xs">
              セッションの取得に失敗しました。通信環境を確認して、画面を再読み込みしてください。
            </p>
          )}
        </div>
      )}

      {uid ? (
        <PostForm
          authorUid={uid}
          districtId={district.id}
          defaultNickname={nickname || `anon-${uid.slice(0, 6)}`}
          photoAcknowledged={photoNoticeAcknowledged}
          onRequestPhotoNotice={() => onPhotoNoticeOpen(true)}
          sessionId={currentSessionId}
          allowPostingWithoutSession={district.allowPostingWithoutSession ?? false}
          userSettingsIncomplete={userSettingsIncomplete}
          postAreaCenter={district.postAreaCenter ?? null}
          postAreaRadiusM={district.postAreaRadiusM ?? null}
          postFields={resolvePostFields(district)}
          onPosted={(id) => {
            // オフライン (outbox) のときも outbox を更新して info toast を出す。
            if (id.startsWith('outbox:')) {
              void refreshOutbox();
              toast('info', 'オフラインのため未送信として保存しました（地図に表示されます）');
            }
            // Sprint BG-DataSettings 26: 投稿成功後はマップタブに自動遷移 (ユーザー要望)。
            // オフライン投稿も同様に遷移し、未送信 pending マーカーを即座に確認できるようにする
            // (以前は outbox 時に return して遷移せず、投稿画面に留まり地図に出ないと誤認されていた)。
            const mapPath = district.urlToken ? `/m/${district.urlToken}/map` : '/map';
            navigate(mapPath);
          }}
        />
      ) : (
        <p className="text-sm text-slate-500 py-4">サインインを待っています...</p>
      )}

      <OutboxList
        items={outboxState.items}
        onCancelled={() => void refreshOutbox()}
        onRetry={async (id) => {
          const result = await retryOneOutbox(id);
          if (result.ok) {
            toast('success', '送信しました');
          } else if (result.notFound) {
            toast('info', '対象 item が見つかりませんでした');
          } else {
            // Sprint BF Part 5: 文字列 lastError を AppError 経由で正規化 (sub-class prefix 付き)。
            const reason = result.lastError ?? 'unknown';
            const appErr = fromUnknownError(new Error(reason));
            toast('error', `送信失敗: ${formatErrorForToast(appErr)}`);
          }
          return result;
        }}
      />
    </div>
  );
}
